import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loading, EmptyState } from "@/components/common/Loading";

// Otter Assist admin — the Otter-Assist-scoped audit browse over the EXISTING platform audit store.
// A Moderator sees only their own actions. Sensitive text is never in an audit row.

const LABEL = {
  "otter_assist.assist.pause": "Paused an Assist",
  "otter_assist.assist.resume": "Resumed an Assist",
  "otter_assist.assist.cancel": "Cancelled an Assist",
  "otter_assist.access.disable": "Disabled Otter Assist access",
  "otter_assist.access.restore": "Restored Otter Assist access",
  "otter_assist.override.apply": "Applied an override",
  "otter_assist.override.revoke": "Revoked an override",
  "otter_assist.control.set": "Changed an operational control",
  "otter_assist.provider.configure": "Changed provider configuration",
  "otter_assist.provider.test": "Tested the provider connection",
  "otter_assist.grant.set": "Changed an admin scope",
  "otter_assist.sensitive_view.helper_note": "Revealed helper messages",
  "otter_assist.sensitive_view.delivered_recipient_identity": "Revealed recipient identities",
  "otter_assist.sensitive_view.candidate_diagnostics": "Opened candidate diagnostics",
};

export function OaAudit() {
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState("");
  const [applied, setApplied] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["oa-admin-audit", page, applied],
    queryFn: async () => (await api.get("/admin/otter-assist/audit", {
      params: { page, page_size: 25, target_id: applied || undefined },
    })).data,
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-3" data-testid="oa-admin-audit">
      <form className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(target.trim()); }}>
        <Input value={target} onChange={(e) => setTarget(e.target.value)}
          placeholder="Filter by Assist ID, user ID or control key"
          className="h-9 max-w-md text-[13px]" data-testid="oa-audit-filter-input" />
        <Button type="submit" size="sm" className="h-9" data-testid="oa-audit-filter-submit">
          Filter
        </Button>
      </form>

      {isLoading ? <Loading /> : !data?.items?.length ? (
        <EmptyState icon={ScrollText} title="No administrative actions yet"
          description="Every Otter Assist admin action is recorded here."
          testId="oa-audit-empty" />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row) => (
                <TableRow key={`${row.action}-${row.created_at}-${row.target_id}`}
                  data-testid={`oa-audit-row-${row.target_id}`}>
                  <TableCell className="whitespace-nowrap text-[11px]">
                    {row.created_at?.slice(0, 16).replace("T", " ")}
                  </TableCell>
                  <TableCell className="text-[12px]">
                    {row.admin_name || row.admin_email || row.admin_id}
                  </TableCell>
                  <TableCell className="text-[12px]">
                    <Badge variant="secondary">{LABEL[row.action] || row.action}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate font-mono text-[10px]">
                    {row.target_id}
                  </TableCell>
                  <TableCell className="max-w-[240px] text-[12px]">
                    {row.meta?.reason || "—"}
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">
                    {row.meta?.before !== undefined || row.meta?.after !== undefined
                      ? `${row.meta?.before ?? "—"} → ${row.meta?.after ?? "—"}`
                      : (row.meta?.silent ? "member not notified" : "—")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {data && data.total > data.page_size && (
        <div className="flex items-center justify-between text-[12px] text-muted-foreground">
          <span data-testid="oa-audit-total">{data.total} entries</span>
          <span className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-7" disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)} data-testid="oa-audit-prev">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span>{page} / {pages}</span>
            <Button size="sm" variant="outline" className="h-7" disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)} data-testid="oa-audit-next">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}

export default OaAudit;
